import { env } from '$env/dynamic/public';
import type {
  EventAlertsResponse,
  GQLError,
  Race,
  RaceBetfairUpdateResponse,
  RacePriceUpdateResponse,
  RaceUpdateResponse
} from '$types/races';

import { createClient } from 'graphql-ws';
import { get, writable } from 'svelte/store';

export function createSubscriptionClient(accessToken: string) {
  const token = writable(accessToken);

  const client = writable(
    createClient({
      url: env.PUBLIC_BETWATCH_WS_ENDPOINT,
      keepAlive: 10_000,
      shouldRetry: () => true,
      retryAttempts: 100,
      connectionParams: async () => {
        return {
          token: get(token)
        };
      }
    })
  );

  const { subscribe } = client;

  function updateAccessToken(newAccessToken: string) {
    token.set(newAccessToken);
  }

  function genericSubscribe(
    query: string,
    variables: any, // eslint-disable-line
    callback: any // eslint-disable-line
  ) {
    if (client) {
      return get(client).subscribe({ query, variables }, callback);
    }
    return undefined;
  }

  function subscribeRacePriceUpdates(
    race: Race,
    callback: {
      next: (data: RacePriceUpdateResponse) => void;
      error: (error: GQLError) => void;
      complete: () => void;
    }
  ) {
    return genericSubscribe(
      `subscription PriceUpdates($id: ID!, $bookmakers: [String!]) {
              priceUpdates(id: $id, bookmakers: $bookmakers) {
                  id
                  bookmaker
                  fixedPlace {
                  price
                  lastUpdated
                  }
                  fixedWin {
                  price
                  lastUpdated
                  }
              }
          }
      `,
      {
        id: race.id
      },
      callback
    );
  }

  function subscribeRaceBetfairUpdates(
    race: Race,
    callback: {
      next: (data: RaceBetfairUpdateResponse) => void;
      error: (error: GQLError) => void;
      complete: () => void;
    }
  ) {
    return genericSubscribe(
      `subscription BetfairUpdates($id: ID!) {
              betfairUpdates(id: $id) {
                id
                sp
                totalMatched
                marketTotalMatched
                lastPriceTraded
                back {
                  price
                  size
                  lastUpdated
                }
                lay {
                  price
                  size
                  lastUpdated
                }
              }
            }
      `,
      {
        id: race.id
      },
      callback
    );
  }

  function subscribeRaceUpdates(
    dateFrom: Date | string,
    dateTo: Date | string,
    callback: {
      next: (data: RaceUpdateResponse) => void;
      error: (error: GQLError) => void;
      complete: () => void;
    }
  ) {
    return genericSubscribe(
      `subscription RacesUpdates($dateFrom: String!, $dateTo: String!) {
              racesUpdates(dateFrom: $dateFrom, dateTo: $dateTo) {
                id
                status
                startTime
                results
              }
            }
      `,
      {
        dateFrom:
          typeof dateFrom === 'string' ? dateFrom : dateFrom.toISOString(),
        dateTo: typeof dateTo === 'string' ? dateTo : dateTo.toISOString()
      },
      callback
    );
  }

  function subscribeEventAlerts(callback: {
    next: (data: EventAlertsResponse) => void;
    error: (error: GQLError) => void;
    complete: () => void;
  }) {
    return genericSubscribe(
      `subscription EventAlerts {
          eventAlerts {
            id
            eventId
            trigger
            value
          }
        }
        `,
      {},
      callback
    );
  }

  return {
    subscribe,
    genericSubscribe,
    subscribeEventAlerts,
    subscribeRaceBetfairUpdates,
    subscribeRacePriceUpdates,
    subscribeRaceUpdates,
    updateAccessToken
  };
}
